import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Navigate,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';

import { FeedbackLayout } from '@components/templates/FeedbackLayout/FeedbackLayout';

import { evictMutationRoot, submittedCaseResponse } from '@graphql/helpers';

import { SubmittedReportLocationState } from '@utils/hooks/useReportCaseSubmit';

import { getPath } from '@services/RouteService/paths';

import { resetReporterDetails } from '@boot/reactiveVariables';

import {
    ActionButton,
    CaseId,
    CaseIdTitle,
    CaseIdWrapper,
    ContainerActions,
    ContainerInner,
    Description,
    Divider,
    InfoSection,
    SubmittedContainer,
    Title,
} from './ReportSubmittedPage.styled';

export const ReportSubmittedPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { caseUuid } = useParams<{ caseUuid: string }>();

    /*
     * Present only when the guest flow submitted through the PIN step - a
     * logged-in submission has no updates address and gets no line about one.
     *
     * Captured into component state on the first render because the effect
     * below immediately strips it out of the router: navigation state
     * serialises into `history.state` and survives in session history, so on
     * a shared device the next user pressing Back would otherwise re-render
     * this page WITH the reporter's address. That covers a same-document
     * Back, where React remounts from the (by-then-stripped) history state -
     * but a cross-document navigation away (a typed URL, an external link)
     * can instead be served from the browser's back-forward cache on Back:
     * the DOM and this captured state are restored live, with no remount and
     * no re-read of history state. The pagehide/visibilitychange effect below
     * clears this state before the page can be frozen into that cache, so
     * either path leaves the address gone and the case ID alone.
     */
    const [updatesEmailOutcome, setUpdatesEmailOutcome] = useState(
        () =>
            (location.state as SubmittedReportLocationState | null)
                ?.updatesEmailOutcome,
    );

    const submittedCaseReport = submittedCaseResponse(caseUuid ?? '');

    useEffect(() => {
        resetReporterDetails();
        /*
         * Make sure the cache holds no mutation footprints either (the raw
         * submit variables - address and PIN - pass through ROOT_MUTATION
         * field keys). Apollo already drops them when each mutation
         * completes; this pins that invariant here, at the boundary. The
         * case's own uuid/caseId entity survives the eviction, so returning
         * here still shows the case ID - and nothing more.
         */
        evictMutationRoot();
        if (submittedCaseReport && location.state != null) {
            navigate(`${location.pathname}${location.search}${location.hash}`, {
                replace: true,
                state: null,
            });
        }
    }, []);

    useEffect(() => {
        /*
         * A cross-document navigation away from this page can be frozen into
         * the browser's back-forward cache instead of being torn down - the
         * live DOM and this component's state (including the captured
         * address) survive as-is, and pressing Back restores them without a
         * remount. `pagehide` fires on that freeze; `visibilitychange` to
         * `hidden` covers the same moment on platforms that skip pagehide
         * (and the tab-switch/backgrounding cases besides). Clearing the
         * outcome here means a bfcache restore has nothing left to show -
         * the case ID stays, since it is read fresh from the cache on every
         * render rather than captured into state.
         */
        const clearOutcome = () => setUpdatesEmailOutcome(undefined);
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') clearOutcome();
        };

        window.addEventListener('pagehide', clearOutcome);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('pagehide', clearOutcome);
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        };
    }, []);

    if (!submittedCaseReport)
        return <Navigate to={getPath({ path: '/new-conversation' })} />;

    return (
        <FeedbackLayout
            iconProps={{
                icon: 'SvgThankYouCircle',
                size: 86,
            }}
        >
            <SubmittedContainer>
                <ContainerInner>
                    <InfoSection>
                        <Title>{t('REPORT_SUBMITTED_TITLE')}</Title>
                        <Description>{t('REPORT_SUBMITTED_INFO')}</Description>
                        {updatesEmailOutcome && (
                            /* Carries the reporter's address, so it is excluded
                             * from capture like the email fields themselves. */
                            <Description
                                className="htmlToImageIgnore"
                                data-testid="text_updates-email-outcome"
                            >
                                {updatesEmailOutcome.confirmedAddress
                                    ? t('REPORT_SUBMITTED_UPDATES_CONFIRMED', {
                                          address:
                                              updatesEmailOutcome.confirmedAddress,
                                      })
                                    : t('REPORT_SUBMITTED_UPDATES_NONE')}
                            </Description>
                        )}
                        <CaseIdWrapper>
                            <CaseIdTitle>{t('REPORT_YOUR_ID')}:</CaseIdTitle>
                            <Divider />
                            <CaseId>{submittedCaseReport?.caseId}</CaseId>
                        </CaseIdWrapper>
                    </InfoSection>
                </ContainerInner>
                <ContainerActions>
                    <ActionButton
                        onClick={() => {
                            navigate(getPath({ path: '/new-conversation' }));
                            resetReporterDetails();
                        }}
                        testId="button_create-new-report"
                        variant={'secondary'}
                    >
                        {t('CREATE_NEW_REPORT')}
                    </ActionButton>
                    <ActionButton
                        onClick={() => navigate(getPath({ path: '/' }))}
                        testId="button_go-to-homepage"
                        variant={'primary'}
                    >
                        {t('GO_TO_HOMEPAGE')}
                    </ActionButton>
                </ContainerActions>
            </SubmittedContainer>
        </FeedbackLayout>
    );
};
